package fr.manyverse.app.infrastructure.imagepicker

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import fr.manyverse.app.domain.image.SupportedImageFormats
import fr.manyverse.app.domain.model.ImageData
import fr.manyverse.app.infrastructure.filepicker.PickerContent
import fr.manyverse.app.infrastructure.filepicker.pickerInitialDirectory
import fr.manyverse.app.infrastructure.filepicker.rememberPickerDialogSettings
import io.github.vinceglb.filekit.PlatformFile
import io.github.vinceglb.filekit.dialogs.FileKitType
import io.github.vinceglb.filekit.dialogs.compose.rememberFilePickerLauncher
import io.github.vinceglb.filekit.extension
import io.github.vinceglb.filekit.mimeType
import io.github.vinceglb.filekit.name
import io.github.vinceglb.filekit.readBytes
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.select_image
import org.jetbrains.compose.resources.stringResource

/**
 * Composable function to create and remember an image picker launcher (FileKit native dialog).
 *
 * Use `rememberImagePickerLauncher` (presentation layer) instead, which reports failures to the
 * user and to Sentry.
 *
 * @param onPicked Callback invoked with the picked image. Not invoked when the user cancels
 * @param onFailure Callback invoked when the picked file cannot be read or converted
 * @return A lambda to launch the image picker
 */
@Composable
fun rememberPlatformImagePickerLauncher(
    onPicked: (ImageData) -> Unit,
    onFailure: (Throwable) -> Unit
): () -> Unit {
    val currentOnPicked by rememberUpdatedState(onPicked)
    val currentOnFailure by rememberUpdatedState(onFailure)
    val scope = rememberCoroutineScope()

    val launcher = rememberFilePickerLauncher(
        type = imagePickerType,
        directory = remember { pickerInitialDirectory(PickerContent.Image) },
        dialogSettings = rememberPickerDialogSettings(PickerContent.Image, stringResource(Res.string.select_image)),
        onError = { currentOnFailure(it) },
        onResult = { file ->
            // null : sélection annulée
            if (file == null) return@rememberFilePickerLauncher
            scope.launch {
                val image = try {
                    ensureSupportedFormat(ImageData(file.readBytes(), file.imageMimeType(), file.name))
                } catch (e: CancellationException) {
                    throw e
                } catch (e: Exception) {
                    currentOnFailure(e)
                    return@launch
                }
                currentOnPicked(image)
            }
        }
    )

    return remember(launcher) { { launcher.launch() } }
}

/**
 * Picker filter: the system photo picker on mobile (any format, converted by
 * [ensureSupportedFormat]), a dialog restricted to [SupportedImageFormats] on desktop and web.
 */
internal expect val imagePickerType: FileKitType

/**
 * Guarantees the returned [ImageData] is one of [SupportedImageFormats]. Mobile re-encodes
 * unsupported formats (e.g. HEIC) to JPEG so the upload pipeline never rejects a valid photo;
 * desktop and web return the image unchanged, their dialog only offers supported formats.
 * Throws [fr.manyverse.app.domain.error.ImageError] if the bytes cannot be decoded.
 */
internal expect suspend fun ensureSupportedFormat(image: ImageData): ImageData

/**
 * Reported MIME type when supported, otherwise the one matching a supported extension
 * (some platforms report nothing or a variant). Anything else is left for
 * [ensureSupportedFormat] to convert.
 */
private fun PlatformFile.imageMimeType(): String {
    val reported = mimeType()?.let { "${it.primaryType}/${it.subtype}" }
    return when {
        reported != null && SupportedImageFormats.isSupported(reported) -> reported
        extension.lowercase() in SupportedImageFormats.fileExtensions -> SupportedImageFormats.mimeFor(extension)
        else -> reported ?: "application/octet-stream"
    }
}
